// --------------------------------------------------------------
// Created On: 2024-03-27
// Author: Dimitra Weinstein
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import { API, MAX_ITEM_QUANTITY } from "../../../../constants/miscellaneous";
import apiRequest from "../../../../utilities/api/apiRequest";
import useRefreshCart from "../../../../hooks/useRefreshCart";
import stringIsInteger from "../../../../utilities/stringIsInteger";
import styles from "./PartsListItem.module.scss";
import LeadTime from "../../../../components/LeadTime/LeadTime";

// Displays individual part item information.
export default function PartsListItem(props: Props): Component {
  const [cartQuantityChangedCounter, setCartQuantityChangedCounter] = useState<number>(0);
  const [inputIsInvalid, setInputIsInvalid] = useState<boolean>(false);
  const showNavigateBomButton = props.parts.length > 0 && !props.bomIsFlat;
  const showCustomizeItemButton = props.isSellable && (props.isImpeller || props.isPaintable);
  const showAddToCartButton = props.isSellable && !props.isImpeller;
  // Refresh the Redux store's cart quantity whenever an item is added to the cart.
  useRefreshCart(() => {
    return cartQuantityChangedCounter > 0;
  }, [cartQuantityChangedCounter]);

  // Ensure part has valid input
  function partIsValid(quantity: string): boolean {
    if (
      [...quantity].find((char) => isNaN(parseInt(char, 10))) ||
      isNaN(parseInt(quantity, 10)) ||
      parseInt(quantity, 10) > MAX_ITEM_QUANTITY ||
      parseInt(quantity, 10) <= 0
    ) {
      setInputIsInvalid(true);
      props.onAddToCartError(`Quantity must be a number between 1 and ${MAX_ITEM_QUANTITY}.`);
      return false;
    } else {
      setInputIsInvalid(false);
      return true;
    }
  }

  // Validates that only numbers are able to be input in quantity field prior to updating.
  function validateQuantity(quantity: string) {
    if ((parseInt(quantity, 10) > 0 || quantity === "") && quantity.indexOf(".") === -1 && stringIsInteger(quantity)) {
      props.onQuantityChange(quantity);
    }
  }

  // Add item to cart.
  async function addItemToCart(partId: number, quantity: string, description: string) {
    if (partIsValid(quantity)) {
      const requestBody = {
        replaceAll: false,
        items: [
          {
            partId: partId,
            paintColorId: props.isPaintable ? props.defaultPaintColorId : null,
            quantity: parseInt(quantity, 10),
            impellerTrim: null,
            impellerAngle: null,
            units: props.units,
          },
        ],
      };
      const [response, responseBody] = await apiRequest(`${API}/cart`, "PUT", requestBody);
      if (response.ok && responseBody) {
        setCartQuantityChangedCounter((previous) => previous + 1);
        quantity > "1"
          ? props.onAddToCart(`${quantity} ${description}'s added to shopping cart.`)
          : props.onAddToCart(`${quantity} ${description} added to shopping cart.`);
      }
    }
  }

  // Modify part number on mouse hover and pass modified part number to onHover().
  function handleMouseEnter(partNumber: string): void {
    props.onHover(partNumber);
  }

  return (
    <tr
      data-test="parts-list-item-row"
      className={props.isHovered ? styles.listItemRowHover : styles.listItemRow}
      onMouseEnter={() => handleMouseEnter(props.partNumber)}
      onMouseLeave={() => props.onHover(null)}
    >
      <td data-test="parts-list-item-description" className="align-content-center">
        {props.description}
      </td>
      <td data-test="parts-list-item-part-number" className="align-content-center">
        {props.partNumber}
      </td>
      <td data-test="parts-list-item-build-quantity" className="align-content-center">
        {props.buildQuantity === "0" ? "1" : props.buildQuantity}&nbsp;{props.units}
      </td>
      <td data-test="parts-list-item-order-quantity" className="align-content-center">
        {props.isSellable && (
          <input
            className={`form-control ${styles.orderQuantity} ${inputIsInvalid ? styles.inputError : ""}`}
            data-test="item-quantity-input"
            type="text"
            maxLength={10}
            value={props.orderQuantity}
            onChange={(e) => validateQuantity(e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
          />
        )}
      </td>
      {props.isSellable && props.price !== null ? (
        <Fragment>
          {props.companyDiscount !== null ? (
            <Fragment>
              <td data-test="parts-list-item-price" className="align-content-center">
                &#36;
                {props.price.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
              <td data-test="parts-list-item-discounted-price" className="align-content-center">
                &#36;
                {(props.price - props.price * (props.companyDiscount / 100)).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
            </Fragment>
          ) : (
            <Fragment>
              <td data-test="parts-list-item-price" className="align-content-center">
                &#36;
                {props.price.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
              <td data-test="parts-list-item-discounted-price" className="align-content-center">
                &#36;
                {props.price.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
            </Fragment>
          )}
        </Fragment>
      ) : (
        <Fragment>
          <td data-test="parts-list-item-price" className="align-content-center">
            N/A
          </td>
          <td data-test="parts-list-item-discounted-price" className="align-content-center">
            N/A
          </td>
        </Fragment>
      )}

      {props.weight !== null && props.weight !== 0 ? (
        <td data-test="parts-list-item-weight" className="align-content-center">
          {props.weight}&nbsp;lbs
        </td>
      ) : (
        <td data-test="parts-list-item-weight" className="align-content-center">
          N/A
        </td>
      )}

      <td data-test="parts-list-item-ship-time" className="align-content-center">
        {props.isSellable && (
          <LeadTime
            quantity={props.orderQuantity}
            partNumber={props.partNumber}
            className=""
            paintColorId={props.isPaintable ? props.defaultPaintColorId : null}
            isImpeller={props.isImpeller}
            impellerAngle=""
            impellerTrim=""
            onLeadTimeCalculated={() => {
              /* Do nothing. */
            }}
          />
        )}
      </td>

      {/* Conditionally render part list item buttons */}
      <td className="align-content-center">
        <div className="row justify-content-end align-items-center g-1 my-0">
          {showNavigateBomButton && (
            <div className="col-auto ">
              <button
                data-test="navigate-btn-bottom"
                className="btn btn-primary btn-sm my-1 my-xxl-0"
                onClick={() => props.onNavigate([...props.bomPath, props.partNumber])}
              >
                <i className="fa fa-fw fa-eye" />
              </button>
            </div>
          )}
          {showCustomizeItemButton && (
            <div className="col-auto">
              <button
                data-test="customize-part-btn"
                type="button"
                className="btn btn-primary btn-sm my-1 my-xxl-0"
                onClick={() => props.onCustomizePart(props.partId)}
              >
                <i className="fa fa-fw fa-wrench" />
              </button>
            </div>
          )}
          {showAddToCartButton && (
            <div className="col-auto">
              <button
                data-test="shopping-cart-btn"
                type="button"
                onClick={() => {
                  addItemToCart(props.partId, props.orderQuantity, props.description);
                }}
                className="btn btn-primary btn-sm my-1 my-xxl-0"
              >
                <i className="fa fa-fw fa-shopping-cart" />
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

interface Props {
  partId: number;
  bomPath: string[];
  isHovered: boolean;
  partNumber: string;
  description: string;
  unitId: number;
  units: string;
  price: number;
  buildQuantity: string;
  orderQuantity: string;
  weight: number;
  isSellable: boolean;
  hasOtherMaterial: boolean;
  isImpeller: boolean;
  bomIsFlat: boolean;
  companyDiscount: number | null;
  isPaintable: boolean;
  defaultPaintColorId: number | null;
  parts: PartFromApi[];
  onQuantityChange: (orderQuantity: string) => void;
  onCustomizePart: (partId: number) => void;
  onHover: (partNumber: string | null) => void;
  onNavigate: (bomPath: string[]) => void;
  onAddToCartError: (message: string) => void;
  onAddToCart: (message: string) => void;
}

interface PartFromApi {
  partId: number;
  partNumber: string;
  description: string;
  unitId: number;
  units: string;
  price: number;
  buildQuantity: string;
  weight: number;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  hasOtherMaterial: boolean;
  parts: PartFromApi[];
}
